/**
 * Appointments.js
 * Tela de Agendamentos — versão definitiva (calendário limpo, novo visual).
 *
 * - Toda a lógica de negócio preservada do AppointmentsV2.js.
 * - Usa Calendar como componente de calendário.
 * - Popups antigos de agendamento removidos — substituídos pelos componentes v2.
 * - SlotPopover contextual no desktop ao clicar em slot vazio.
 * - AppointmentDrawer, AppointmentDetailPanel, BlockSlotDrawer, ReschedulePanel ativos.
 * - CreateInvoicePopup mantido para fluxo de fatura.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './Appointments.module.css';
import { DataProviderInstance } from '../../api/DataProvider';
import { MoveAppointmentPaymentAPI } from '../../api/API';
import Toast from '../../components/support/Toast';
import CalendarConfig, { getSlotIntervalMinutes } from '../../consts/CalendarConfig';
import AppConfig from '../../consts/AppConfig';
import { getCurrentUser } from '../../utils/RoleManager';
import { useWaitingListOpportunities, getSlotOpportunity, anyProfessionalSlotOpportunity } from './v2/hooks/useWaitingListOpportunities';
import { useAgendaWhatsAppSend } from './v2/hooks/useAgendaWhatsAppSend';
import { useSavedAppointmentFocus } from './v2/hooks/useSavedAppointmentFocus';

// ── Calendário novo ───────────────────────────────────────────────────────────
import Calendar from './Calendar/Calendar';
import MonthlyCalendar from './MonthlyCalendar/MonthlyCalendar';
import AppointmentsMobile from './AppointmentsMobile';

// ── Filtros ───────────────────────────────────────────────────────────────────
import CalendarDatePicker from './components/filters/calendarDatePicker/CalendarDatePicker';
import QuickFilterDropdown from './components/filters/QuickFilterDropdown';
import ResourceMenu from './components/ResourceMenu';
import { NARROW_COLUMN_WIDTH_PX, WIDE_COLUMN_WIDTH_PX } from './Calendar/resourceColumns';

// ── Popup de fatura (mantido) ─────────────────────────────────────────────────
import CreateInvoicePopup from '../../popup/CreateInvoicePopup/CreateInvoicePopup';

// ── Novos componentes de interação v2 (drawers/panels modernos) ───────────────
import SlotPopover from './v2/components/SlotPopover/SlotPopover';
import AppointmentDrawer from './v2/components/AppointmentDrawer/AppointmentDrawer';
import AppointmentDetailPanel from './v2/components/AppointmentDetailPanel/AppointmentDetailPanel';
import BlockSlotDrawer from './v2/components/BlockSlotDrawer/BlockSlotDrawer';
import ReserveSlotDrawer from './v2/components/ReserveSlotDrawer/ReserveSlotDrawer';
import ReservationPanel from './v2/components/ReserveSlotDrawer/ReservationPanel';
import ReschedulePanel from './v2/components/ReschedulePanel/ReschedulePanel';
import CreateAppointmentPicker from './v2/components/CreateAppointmentPicker/CreateAppointmentPicker';
import BottomSheet from './v2/components/BottomSheet/BottomSheet';
import useDayRooms from '../../components/day-rooms/useDayRooms';
import { summarizeDayRooms, toDayKey, resolveRoomFor } from '../../components/day-rooms/dayRooms';

// ── Constantes ────────────────────────────────────────────────────────────────
import AppointmensState from '../../consts/AppointmentsState';
import { getLegendItems } from '../../consts/AppointmentStatusColors';
import { sendAppointmentCreatedWhatsApp } from '../../utils/appointmentWhatsAppUtils';

// ── MUI ───────────────────────────────────────────────────────────────────────
import {
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';

// ── Ícones MUI ────────────────────────────────────────────────────────────────
import FilterListIcon from '@mui/icons-material/FilterList';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import CalendarViewWeekOutlinedIcon from '@mui/icons-material/CalendarViewWeekOutlined';
import CalendarViewDayOutlinedIcon from '@mui/icons-material/CalendarViewDayOutlined';
import CalendarViewMonthOutlinedIcon from '@mui/icons-material/CalendarViewMonthOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import ViewHeadlineOutlinedIcon from '@mui/icons-material/ViewHeadlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import ViewCompactOutlinedIcon from '@mui/icons-material/ViewCompactOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { appointmentProcedureIds, isSlotAvailable } from './v2/utils/appointmentUtils';
import { ATTENDANCE_ONLINE, normalizeAttendanceMode } from './v2/utils/attendanceMode';
import { calendarHourRange } from '../../utils/resourceSchedule';

// A partir de quantas agendas a tela trata a conta como "muitas agendas": aviso
// no Diário/Semanal, Profissionais como visão padrão e o atalho "Localizar profissional"
const MANY_AGENDAS_MIN = 6;

const MONTH_NAMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
];
const WEEKDAY_NAMES = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];

// ─── Hook de animação de contagem ────────────────────────────────────────────
function useCountUp(target, duration = 600) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(target);

  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (from === to) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;

    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * ease));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return display;
}

// Componente que exibe número com animação de contagem
const AnimatedNumber = ({ value, suffix = '' }) => {
  const animated = useCountUp(value);
  return <>{animated}{suffix}</>;
};

// ─────────────────────────────────────────────────────────────────────────────
// ── Helper: converte "YYYY-MM-DD HH:mm:ss" → { date: Date, hour, minute } ──
function parseDateToSlot(dateStr) {
  if (!dateStr) return null;
  const [datePart, timePart] = dateStr.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = (timePart || '00:00').split(':').map(Number);
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  return { date, hour, minute };
}

// ── Helper: converte appointment do DataProvider → formato v2 ─────────────────
function normalizeAppointment(data) {
  if (!data) return null;
  // Garante que duration seja número (pode vir como "00:50:00" ou 50)
  let duration = data.duration;
  if (typeof duration === 'string' && duration.includes(':')) {
    const [h, m] = duration.split(':').map(Number);
    duration = h * 60 + m;
  }
  return { ...data, duration: Number(duration) || 50 };
}

function Appointments() {
  const { CompanyConfig, GetAppointments, UpdateAppointment, SetAppointment, GetAppointmentsKpi, UpdateWaitingList, GetPatientById, GetDayRooms, session } = DataProviderInstance();

  // Avisos da agenda pelo sistema (Profissional ou superior / número próprio) em vez
  // do WhatsApp Web. O Toast fica aqui porque os drawers fecham ao salvar.
  const {
    canSendViaSystem: canSendWhatsAppViaSystem,
    notifyPatient: notifyPatientWhatsApp,
    toast: whatsAppToast,
  } = useAgendaWhatsAppSend();

  // Falha ao passar o pagamento no reagendamento (o drawer já fechou)
  const [rescheduleWarning, setRescheduleWarning] = useState(null);
  const rescheduleWarningToast = (
    <Toast
      open={!!rescheduleWarning}
      message={rescheduleWarning || ''}
      severity="warning"
      onClose={() => setRescheduleWarning(null)}
    />
  );

  // Agendar pela lista de espera com agenda "Qualquer": diz em qual agenda entrou
  // (ou que nenhuma estava livre), porque o drawer já fechou
  const [waitingListNotice, setWaitingListNotice] = useState(null);
  const waitingListToast = (
    <Toast
      open={!!waitingListNotice}
      message={waitingListNotice?.message || ''}
      severity={waitingListNotice?.severity || 'info'}
      onClose={() => setWaitingListNotice(null)}
    />
  );

  // Celular: depois de salvar, a agenda vai para o dia do agendamento, destaca o
  // cartão e avisa "Agendado para dd/MM às HH:mm" (goToDateInCurrentView está
  // declarada mais abaixo, junto dos handlers de navegação; function é içada).
  const savedFocus = useSavedAppointmentFocus({ goToDate: goToDateInCurrentView });

  // "Ver na agenda" do Início abre /schedule/appointments?date=YYYY-MM-DD: a
  // agenda já começa no período desse dia.
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  useEffect(() => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateParam || '');
    if (!match) return;
    goToDateInCurrentView(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParam]);

  // ── Estado do drawer de recorrência ────────────────────────────────────────
  // Controla se o drawer de novo agendamento está no modo recorrente (duas colunas)
  const [isDrawerRecurring, setIsDrawerRecurring] = useState(false);

  // ── Estado de layout ────────────────────────────────────────────────────────
  // isMobile  → celular (< 1010px): usa AppointmentsMobile + CalendarMobile
  // isTablet  → tablet (1010px–1566px): usa layout desktop (Calendar.js) em tela menor
  // isDesktop → desktop (≥ 1567px): layout desktop completo com sidebar fixa visível
  const [isMobile, setIsMobile] = useState(window.innerWidth < AppConfig.MinScreenSizeX);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= AppConfig.MinScreenSizeX && window.innerWidth <= 1566);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ── Estado de calendário ────────────────────────────────────────────────────
  const [currentAppointment, setCurrentAppointment] = useState(null);
  // Modo de visualização: 'daily' | 'weekly' | 'monthly' | 'professionals' (um dia,
  // uma coluna por profissional, com rolagem lateral no celular).
  // Vale o último escolhido (cv3-viewMode); sem escolha, o celular abre em Dia e o
  // computador em Semana.
  const [viewMode, setViewMode] = useState(() => {
    const fallback = window.innerWidth < AppConfig.MinScreenSizeX ? 'daily' : 'weekly';
    try { return localStorage.getItem('cv3-viewMode') || fallback; } catch { return fallback; }
  });
  const [viewDaysCount, setViewDaysCount] = useState(() => (viewMode === 'daily' || viewMode === 'professionals' ? 1 : 7));
  const [page, setPage] = useState(0);
  // Estado para view mensal
  const [monthlyAppointments, setMonthlyAppointments] = useState([]);
  const [monthlyCurrentDate, setMonthlyCurrentDate] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d;
  });
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('cv3-density') || 'comfortable'; } catch { return 'comfortable'; }
  });
  const SLOT_HEIGHTS = { comfortable: 80, compact: 48 };
  const slotHeight = SLOT_HEIGHTS[density] || 80;

  function handleDensityChange(val) {
    try { localStorage.setItem('cv3-density', val); } catch {}
    setDensity(val);
  }

  // Largura das colunas do modo "Profissionais": 'wide' (160px) | 'narrow' (100px)
  const [columnWidth, setColumnWidth] = useState(() => {
    try { return localStorage.getItem('cv3-columnWidth') || 'wide'; } catch { return 'wide'; }
  });
  function handleColumnWidthChange(val) {
    try { localStorage.setItem('cv3-columnWidth', val); } catch {}
    setColumnWidth(val);
  }

  // "Localizar profissional" (modo "Profissionais"): o Calendar rola até a coluna e a
  // destaca; o nonce muda a cada pedido para valer mesmo na mesma agenda
  const [focusColumn, setFocusColumn] = useState({ id: null, nonce: 0 });
  // Menu de agendas: { anchor, mode } com mode 'goto' (rolar até a coluna) ou
  // 'pick' (escolher uma só agenda no filtro, pelo aviso de muitas agendas)
  const [resourceMenu, setResourceMenu] = useState(null);
  // Aviso "com N agendas, o dia fica mais claro por profissional": fechado vale a sessão
  const [manyAgendasHintDismissed, setManyAgendasHintDismissed] = useState(() => {
    try { return sessionStorage.getItem('cv3-manyAgendasHintDismissed') === '1'; } catch { return false; }
  });
  function dismissManyAgendasHint() {
    try { sessionStorage.setItem('cv3-manyAgendasHintDismissed', '1'); } catch {}
    setManyAgendasHintDismissed(true);
  }
  const _today = GetCurrentDate();
  const [popupId, setPopupId] = useState(-1);

  // ── Estado dos drawers v2 ───────────────────────────────────────────────────
  // drawerType: null | 'slot_popover' | 'new-appointment' | 'block' | 'reserve' | 'reservation' | 'detail' | 'reschedule' | 'create-picker'
  const [drawerType, setDrawerType] = useState(null);
  const [drawerSlot, setDrawerSlot] = useState(null);       // { date, hour, minute }
  const [drawerAppointment, setDrawerAppointment] = useState(null); // agendamento selecionado
  const [reschedulingAppointment, setReschedulingAppointment] = useState(null);
  // Pré-preenchimento do novo agendamento (reserva cujo telefone já é de um paciente)
  const [appointmentInitialValues, setAppointmentInitialValues] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  // Popup aberto pelo selo "Qualquer profissional" do eixo de horas (modo "Profissionais")
  const [slotAnyProfessional, setSlotAnyProfessional] = useState(false);
  // Fantasma de bloqueio — exibido no calendário enquanto o drawer de bloqueio está aberto
  const [ghostAppointment, setGhostAppointment] = useState(null);
  // Ref usado para evitar que onClose do SlotPopover sobrescreva uma transição de drawer
  // O SlotPopover chama onClose() após cada ação de botão — este ref impede o fechamento
  // quando a ação já navegou para outro drawerType no mesmo ciclo de render.
  const popoverNavigatingRef = useRef(false);

  // ── Oportunidades da lista de espera ───────────────────────────────────────
  // Calcula o período visível para buscar oportunidades
  const visiblePeriod = useMemo(() => {
    if (viewMode === 'monthly') return { startDate: null, endDate: null };
    const startDate = getCurrentViewDate(_today, page, viewDaysCount);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + viewDaysCount - 1);
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return { startDate: fmt(startDate), endDate: fmt(endDate) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, viewDaysCount, viewMode]);

  const { opportunitiesMap, reload: reloadOpportunities } = useWaitingListOpportunities({
    startDate: visiblePeriod.startDate,
    endDate: visiblePeriod.endDate,
    viewMode,
  });

  // ── reloadCounter ──────────────────────────────────────────────────────────
  // Incrementado por callbacks de drawers (ex: closeDrawerAndRefresh) para forçar
  // o recarregamento dos agendamentos via useEffect, garantindo que LoadData seja
  // chamado com os valores ATUAIS de page/viewDaysCount/viewMode (sem stale closure).
  // Esse padrão é o idiomático em React para evitar bugs de closure obsoleta —
  // o efeito sempre executa com as variáveis no escopo do render mais recente.
  // IMPORTANTE: declarado ANTES de qualquer useCallback que o consuma, para evitar TDZ.
  const [reloadCounter, setReloadCounter] = useState(0);
  const triggerReload = useCallback(() => setReloadCounter(c => c + 1), []);

  const closeDrawer = useCallback(() => {
    setDrawerType(null);
    setDrawerSlot(null);
    setDrawerAppointment(null);
    setGhostAppointment(null);
    setAppointmentInitialValues(null);
  }, []);

  // Reserva: o telefone já é de um paciente → novo agendamento já preenchido
  const handleScheduleExistingPatient = useCallback((patient, { resourceId, procedureId, procedures, roomId }) => {
    setGhostAppointment(null);
    setAppointmentInitialValues({ patient, resourceId, procedureId, procedures, roomId });
    setDrawerType('new-appointment');
  }, []);

  // Novo agendamento pelo picker: o passo 2 vem com a agenda escolhida no passo 1
  const handlePickerSlotSelected = useCallback(({ resourceId, ...slot }) => {
    setDrawerSlot(slot);
    setAppointmentInitialValues(resourceId ? { resourceId } : null);
    setDrawerType('new-appointment');
  }, []);

  const closeDrawerAndRefresh = useCallback(() => {
    closeDrawer();
    // Dispara reload via reloadCounter — o useEffect que chama LoadData reage a esse counter
    // e sempre executa com os valores atuais de page/viewDaysCount/viewMode no escopo do render
    triggerReload();
  }, [closeDrawer, triggerReload]);

  // Handler para criar agendamento a partir da lista de espera
  const handleScheduleFromWaitingList = useCallback(async (patient, slot) => {
    return new Promise((resolve) => {
      const dateStr = `${slot.date} ${slot.time}:00`;
      const waitingItem = patient.waitingListItem;
      // Vale a coluna clicada (modo "Profissionais"); sem coluna, a agenda pedida na
      // lista de espera; sem nenhuma, o backend escolhe uma agenda livre no horário
      const columnResourceId = slot?.resourceId ?? appointmentInitialValues?.resourceId ?? null;
      const wantedResourceId = waitingItem?.resourceId || null;
      if (wantedResourceId && columnResourceId != null && String(wantedResourceId) !== String(columnResourceId)) {
        // A coluna só mostra quem pode ser atendido por ela; isto é só uma proteção
        const wanted = CompanyConfig?.Resources?.find(r => String(r.Id) === String(wantedResourceId));
        setWaitingListNotice({
          severity: 'warning',
          message: `Este pedido da lista de espera é para a agenda de ${wanted?.Label || 'outro profissional'}. Abra o horário na coluna dessa agenda.`,
        });
        resolve(null);
        return;
      }
      const resourceId = columnResourceId ?? wantedResourceId;

      // Busca a duração do procedimento no formato original da API ("HH:MM:SS")
      // O backend espera esse formato — não converter para minutos
      const proc = CompanyConfig?.Procedures?.find(p => String(p.Id) === String(waitingItem?.procedureId));
      const rawDuration = proc?.Duration ?? null;
      const rawPrice = proc?.Price ?? null;

      // ── Abre o WhatsApp IMEDIATAMENTE antes da chamada de API ──────────────
      // window.open só é permitido pelo browser em resposta direta a evento de usuário.
      // Com envio pelo sistema a confirmação sai depois de salvar (notifyPatientWhatsApp).
      const patientData = GetPatientById(String(waitingItem?.patientId));
      if (patientData?.phone && !canSendWhatsAppViaSystem) {
        const resource = CompanyConfig?.Resources?.find(r => String(r.Id) === String(resourceId));
        const [datePart, timePart] = dateStr.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [h, m] = (timePart || '00:00').split(':');
        // Garante que countryCode seja string antes de passar para sendAppointmentCreatedWhatsApp
        const countryCode = String(patientData.countryCode || '');
        sendAppointmentCreatedWhatsApp({
          patientPhone: patientData.phone,
          patientName: patientData.name || '',
          countryCode,
          aptDate: new Date(year, month - 1, day),
          time: `${h}:${m}`,
          doctorName: resource?.Label || '',
          price: rawPrice,
          companyConfig: CompanyConfig,
        });
      }

      // Modalidade e link pedidos na entrada (entradas antigas = presencial)
      const attendanceMode = normalizeAttendanceMode(waitingItem?.attendance_mode);
      const isOnline = attendanceMode === ATTENDANCE_ONLINE;

      // Sala como no novo agendamento: sala do dia da agenda > sala padrão > nenhuma
      const saveWithRoom = (roomId) => SetAppointment({
        patient: waitingItem?.patientId,
        resource: resourceId,
        procedure: waitingItem?.procedureId || null,
        date: dateStr,
        status: 'scheduled',
        duration: rawDuration,
        price: rawPrice,
        roomId: isOnline ? null : (roomId || null),
        attendance_mode: attendanceMode,
        meeting_url: isOnline ? (waitingItem?.meeting_url || null) : null,
      }, (response) => {
        // set_appointment.php retorna { status: "SUCCESS", data: { id, ... } }
        const success = response && (
          response.status === 'SUCCESS' ||
          response.success === true ||
          (response.data && response.data.id)
        );
        if (success) {
          const createdId = response?.data?.id ?? null;
          if (patientData?.phone && canSendWhatsAppViaSystem && createdId) {
            notifyPatientWhatsApp({ type: 'created', appointment_ids: [createdId] });
          }
          if (waitingItem?.id) {
            UpdateWaitingList({ id: waitingItem.id, status: 'SCHEDULED' }, () => {});
          }
          if (response?.data?.resourcePicked) {
            const picked = CompanyConfig?.Resources?.find(r => String(r.Id) === String(response.data.resource));
            setWaitingListNotice({ severity: 'info', message: `Agendado na agenda de ${picked?.Label || 'uma agenda livre'}` });
          }
          closeDrawer();
          triggerReload();
          reloadOpportunities();
        } else if (response?.message) {
          setWaitingListNotice({ severity: 'warning', message: response.message });
        }
        resolve(response);
      });

      const rooms = CompanyConfig?.Rooms || [];
      const resource = resourceId != null ? CompanyConfig?.Resources?.find(r => String(r.Id) === String(resourceId)) : null;
      if (isOnline || !resource || rooms.length === 0) {
        saveWithRoom(null);
        return;
      }
      GetDayRooms(slot.date, (result) => {
        const assignments = result?.status === 'SUCCESS' ? (result.data?.assignments || []) : [];
        const roomId = resolveRoomFor(assignments, resource, slot.time).roomId;
        saveWithRoom(rooms.some(r => String(r.Id) === String(roomId)) ? roomId : null);
      });
    });
  }, [SetAppointment, UpdateWaitingList, closeDrawer, triggerReload, reloadOpportunities, CompanyConfig, GetPatientById, GetDayRooms, canSendWhatsAppViaSystem, notifyPatientWhatsApp, appointmentInitialValues]);




  // ── Utilitários de cache ────────────────────────────────────────────────────
  const readCache = (key, fallback) => {
    try {
      const v = localStorage.getItem(key);
      if (v === null) return fallback;
      return JSON.parse(v);
    } catch { return fallback; }
  };
  const writeCache = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
  };

  // Setter com cache automático
  const makeCachedSetter = (key, setter) => (value) => {
    writeCache(key, value);
    setter(value);
  };

  // ── Estado de dados ─────────────────────────────────────────────────────────
  const usingStatesSource = AppointmensState.Filters;
  const [allAppointments, setAllAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);

  // A fila de pendências de convênio abre ?date=...&appointment=ID: assim que o
  // agendamento chega na lista, o detalhe abre sozinho (uma vez por link)
  const appointmentParam = searchParams.get('appointment');
  const openedFromLink = useRef(null);
  useEffect(() => {
    if (!appointmentParam || openedFromLink.current === appointmentParam) return;
    const found = allAppointments.find((item) => String(item.id) === String(appointmentParam));
    if (!found) return;
    openedFromLink.current = appointmentParam;
    OnItemSelectHandler(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentParam, allAppointments]);

  const [filtersByDayOfWeek, _setFiltersByDayOfWeek] = useState(
    readCache('qf-dayOfWeek', CalendarConfig.DaysOfWeek.map((_, i) => i))
  );
  const setFiltersByDayOfWeek = makeCachedSetter('qf-dayOfWeek', _setFiltersByDayOfWeek);

  const [filtersByState, _setFiltersByState] = useState(
    readCache('qf-state', usingStatesSource.map((_, i) => i))
  );
  const setFiltersByState = makeCachedSetter('qf-state', _setFiltersByState);

  // Agendas, salas e procedimentos: guardados por pessoa e empresa, pelo id (não pela
  // posição na lista). Em memória continuam como índices de CompanyConfig, que é o que
  // QuickFilterDropdown e o filtro abaixo usam. As chaves antigas (qf-resources etc.,
  // sem pessoa e por índice) são ignoradas.
  const currentUser = getCurrentUser();
  const filterCacheScope = `${currentUser?.id ?? ''}:${session?.currentCompany ?? ''}`;
  const ownScheduleResourceId = currentUser?.isHealthProfessional && currentUser?.scheduleScope === 'own'
    ? currentUser?.resourceId
    : null;
  const listFilterKey = (name) => `qf-${name}:${filterCacheScope}`;

  const readIdsAsIndexes = (name, list) => {
    const stored = readCache(listFilterKey(name), null);
    if (!Array.isArray(stored)) return list.map((_, i) => i);
    const ids = stored.map(String);
    return list.reduce((acc, item, i) => {
      if (ids.includes(String(item?.Id))) acc.push(i);
      return acc;
    }, []);
  };

  const makeIdCachedSetter = (name, listName, setter) => (value) => {
    const list = CompanyConfig?.[listName] || [];
    const ids = (value || []).filter((i) => list[i]).map((i) => list[i].Id);
    writeCache(listFilterKey(name), ids);
    setter(value);
  };

  const [filtersByProcedures, _setFiltersByProcedures] = useState([]);
  const setFiltersByProcedures = makeIdCachedSetter('procedures', 'Procedures', _setFiltersByProcedures);

  const [filtersByResources, _setFiltersByResources] = useState([]);
  const setFiltersByResources = makeIdCachedSetter('resources', 'Resources', _setFiltersByResources);

  const [filtersByRooms, _setFiltersByRooms] = useState([]);
  const setFiltersByRooms = makeIdCachedSetter('rooms', 'Rooms', _setFiltersByRooms);

  const [showOutsideWorkingHours, setShowOutsideWorkingHours] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // ── Inicialização de filtros (quando CompanyConfig carrega ou a pessoa/empresa muda) ──
  // Sem nada guardado, tudo marcado; ids que não existem mais são ignorados.
  // Quem tem "Só os meus pacientes" abre sempre na própria agenda.
  useEffect(() => {
    if (!CompanyConfig) return;
    const procedures = CompanyConfig.Procedures || [];
    const resources = CompanyConfig.Resources || [];
    const rooms = CompanyConfig.Rooms || [];

    _setFiltersByProcedures(readIdsAsIndexes('procedures', procedures));
    _setFiltersByRooms(readIdsAsIndexes('rooms', rooms));

    const ownIndex = ownScheduleResourceId != null
      ? resources.findIndex((r) => String(r?.Id) === String(ownScheduleResourceId))
      : -1;
    _setFiltersByResources(ownIndex >= 0 ? [ownIndex] : readIdsAsIndexes('resources', resources));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CompanyConfig, filterCacheScope, ownScheduleResourceId]);

  // ── Carregamento de dados mensais ───────────────────────────────────────────
  function LoadMonthlyData(date) {
    const d = date || monthlyCurrentDate;
    const year = d.getFullYear();
    const month = d.getMonth();
    const startDate = new Date(year, month, 1).toISOString().slice(0, 10);
    const endDate = new Date(year, month + 1, 0).toISOString().slice(0, 10);
    GetAppointments(startDate, endDate, (result) => {
      setMonthlyAppointments(result);
    });
  }

  // ── Carregamento de dados ───────────────────────────────────────────────────
  function LoadData() {
    setIsUpdating(true);
    const startDate = getCurrentViewDate(_today, page, viewDaysCount)
      .toISOString()
      .slice(0, 10);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + viewDaysCount - 1);
    const endDateStr = endDate.toISOString().slice(0, 10);

    GetAppointments(startDate, endDateStr, (result) => {
      setAllAppointments(result);

      // Se há um drawer de detalhes aberto, atualiza o drawerAppointment com os dados frescos
      setDrawerAppointment(prev => {
        if (!prev) return prev;
        const fresh = result.find(a => String(a.id) === String(prev.id));
        return fresh ? normalizeAppointment(fresh) : prev;
      });
    });
  }


  useEffect(() => { setFilteredAppointments(allAppointments); }, [allAppointments]);

  useEffect(() => { setIsUpdating(false); }, [filteredAppointments]);

  useEffect(() => {
    // Na semana, no dia e por profissionais o efeito de [viewDaysCount, page, viewMode]
    // já carrega na montagem; chamar aqui também baixava a agenda duas vezes (PERF-01).
    if (viewMode === 'monthly') LoadData();
    handleResize();
    window.addEventListener('resize', handleResize);

    try {
      const savedValue = localStorage.getItem('filterList-workingHours');
      if (savedValue) {
        const parsedValue = JSON.parse(savedValue);
        setShowOutsideWorkingHours(parsedValue.length > 0);
      } else {
        setShowOutsideWorkingHours(false);
        localStorage.setItem('filterList-workingHours', JSON.stringify([]));
      }
    } catch {
      setShowOutsideWorkingHours(false);
    }

    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarrega quando: muda semana/dia/mode, OU quando reloadCounter é incrementado
  // (após criar/editar/deletar agendamento via drawer)
  useEffect(() => {
    if (viewMode !== 'monthly') {
      LoadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDaysCount, page, viewMode, reloadCounter]);

  useEffect(() => {
    if (viewMode === 'monthly') {
      LoadMonthlyData(monthlyCurrentDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, monthlyCurrentDate, reloadCounter]);


  // ── Resize ──────────────────────────────────────────────────────────────────
  // isMobile: telas pequenas (< 1010px) → AppointmentsMobile + CalendarMobile
  // Tablets (1010px–1566px) e desktops (≥ 1567px) usam o layout desktop (Calendar.js)
  function handleResize() {
    const width = window.innerWidth;
    setIsMobile(width < AppConfig.MinScreenSizeX);
    setIsTablet(width >= AppConfig.MinScreenSizeX && width <= 1566);
  }

  // ── Filtros aplicados ───────────────────────────────────────────────────────
  useEffect(() => {
    let result = [...allAppointments];

    result = result.filter((item) => {
      // Bloqueio geral da empresa (resource null/vazio): SEMPRE aparece,
      // independente do filtro de status — não pode ser ocultado pelo filtro de "Bloqueado"
      // get_appointments.php agora retorna null (não "0") quando resource é NULL no banco
      const isGeneralBlock = item.status === 'blocked' && (
        item.resource == null ||
        item.resource === ''
      );
      if (isGeneralBlock) return true;

      return filtersByState.includes(usingStatesSource.indexOf(item.status));
    });

    let proceduresIds = filtersByProcedures
      .filter((i) => CompanyConfig?.Procedures?.[i])
      .map((i) => CompanyConfig.Procedures[i].Id);
    proceduresIds.push(null, '', '0');

    let resourcesIds = filtersByResources
      .filter((i) => CompanyConfig?.Resources?.[i])
      .map((i) => CompanyConfig.Resources[i].Id);
    // Inclui null e '' para agendamentos sem profissional definido,
    // mas NÃO inclui '0' pois 0 pode ser um ID válido de profissional.
    resourcesIds.push(null, '');

    let roomsIds = filtersByRooms
      .filter((i) => CompanyConfig?.Rooms?.[i])
      .map((i) => CompanyConfig.Rooms[i].Id);
    roomsIds.push(null, '', '0');

    result = result.filter((item) => {
      // Bloqueio geral da empresa: resource é null/vazio/string "null" → sempre aparece independente do filtro
      // A API envia "null" (string) quando resource é NULL no banco
      const isGeneralBlock = item.status === 'blocked' && (
        item.resource == null ||
        item.resource === '' ||
        item.resource === 'null'
      );
      if (isGeneralBlock) return true;

      // Bloqueio de profissional específico: só aparece se o profissional estiver no filtro
      const isResourceBlock = item.status === 'blocked' && item.resource != null && item.resource !== '' && item.resource !== 'null';
      if (isResourceBlock) {
        return resourcesIds.includes(item.resource);
      }

      const procedureStillExists = CompanyConfig?.Procedures?.some(
        (p) => p.Id == item.procedure
      );
      if (!procedureStillExists) return true;

      // Vários procedimentos por agendamento: entra se qualquer um estiver no filtro
      const itemProcedures = appointmentProcedureIds(item);
      const procedureMatches = itemProcedures.length === 0
        ? proceduresIds.includes(item.procedure)
        : itemProcedures.some((id) => proceduresIds.some((filterId) => String(filterId) === id));

      return (
        procedureMatches &&
        resourcesIds.includes(item.resource) &&
        roomsIds.includes(item.roomId)
      );
    });

    setFilteredAppointments(result);
  }, [
    allAppointments,
    filtersByState,
    filtersByProcedures,
    filtersByResources,
    filtersByRooms,
    CompanyConfig,
  ]);

  // ── Funções de data ─────────────────────────────────────────────────────────
  function GetCurrentDate() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  function getCurrentViewDate(today, page, viewDaysCount) {
    if (viewDaysCount > 1) {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + page * 7);
      startOfWeek.setHours(0, 0, 0, 0);
      return startOfWeek;
    } else {
      const newDate = new Date(today);
      newDate.setDate(today.getDate() + page * viewDaysCount);
      newDate.setHours(0, 0, 0, 0);
      return newDate;
    }
  }

  function getDaysForCurrentView() {
    const days = [];
    const startDate = getCurrentViewDate(_today, page, viewDaysCount);
    for (let i = 0; i < viewDaysCount; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      if (filtersByDayOfWeek.includes(currentDate.getDay())) {
        days.push(new Date(currentDate));
      }
    }
    return days;
  }

  function GetMonthView() {
    return getCurrentViewDate(_today, page, viewDaysCount).getMonth();
  }

  // ── Helpers de navegação ────────────────────────────────────────────────────
  // Na view diária, pula dias filtrados pelo filtersByDayOfWeek.
  // Retorna o próximo page válido (cujo dia da semana está em filtersByDayOfWeek).
  // Se todos os dias estiverem filtrados, retorna o page original sem pular.
  function getNextValidPage(fromPage, direction) {
    // Só aplica o pulo na view diária
    if (viewDaysCount !== 1) return fromPage + direction;

    // Se não há filtro de dias ativo (todos os 7 dias permitidos), não precisa pular
    if (filtersByDayOfWeek.length === 7) return fromPage + direction;

    // Tenta até 7 passos para encontrar um dia não-filtrado
    let candidate = fromPage + direction;
    for (let attempt = 0; attempt < 7; attempt++) {
      const candidateDate = new Date(_today);
      candidateDate.setDate(_today.getDate() + candidate);
      if (filtersByDayOfWeek.includes(candidateDate.getDay())) {
        return candidate;
      }
      candidate += direction;
    }
    // Fallback: nenhum dia válido encontrado nos próximos 7 — retorna o passo simples
    return fromPage + direction;
  }

  // ── Handlers de navegação ───────────────────────────────────────────────────
  function OnPrevClickHandler() {
    if (viewMode === 'monthly') {
      setMonthlyCurrentDate(prev => {
        const d = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
        return d;
      });
      return;
    }
    setPage(getNextValidPage(page, -1));
  }
  function OnNextClickHandler() {
    if (viewMode === 'monthly') {
      setMonthlyCurrentDate(prev => {
        const d = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
        return d;
      });
      return;
    }
    setPage(getNextValidPage(page, +1));
  }
  function OnTodayClickHandler() {
    if (viewMode === 'monthly') {
      const d = new Date(); d.setDate(1); d.setHours(0,0,0,0);
      setMonthlyCurrentDate(d);
      return;
    }
    setPage(0);
  }

  // Leva a visão atual até uma data: dia → aquele dia; semana → a semana dela;
  // mês → o mês dela. Math.round é à prova de horário de verão (dias de 23h/25h).
  function goToDateInCurrentView(date) {
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    if (viewMode === 'monthly') {
      setMonthlyCurrentDate(new Date(target.getFullYear(), target.getMonth(), 1));
      return;
    }
    const dayMs = 1000 * 60 * 60 * 24;
    if (viewDaysCount > 1) {
      const targetWeekStart = new Date(target);
      targetWeekStart.setDate(target.getDate() - target.getDay());
      const todayWeekStart = new Date(_today);
      todayWeekStart.setDate(_today.getDate() - _today.getDay());
      setPage(Math.round((targetWeekStart - todayWeekStart) / dayMs / 7));
      return;
    }
    setPage(Math.round((target - _today) / dayMs));
  }

  // Conta com muitas agendas abrindo a Agenda pela primeira vez (sem escolha
  // guardada): no computador já entra por profissional, a única visão que
  // funciona com esse volume
  const manyAgendas = (CompanyConfig?.Resources?.length ?? 0) > MANY_AGENDAS_MIN;
  useEffect(() => {
    if (!manyAgendas || isMobile) return;
    let saved = null;
    try { saved = localStorage.getItem('cv3-viewMode'); } catch {}
    if (saved !== null) return;
    OnSelectViewModeHandler(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manyAgendas]);

  // Setas do teclado rolam as colunas do modo "Profissionais" (sem drawer, popup
  // ou menu aberto e fora de campos de texto)
  useEffect(() => {
    if (viewMode !== 'professionals' || drawerType != null || isMobile) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      if (document.querySelector('.MuiModal-root')) return;
      const area = document.querySelector('[data-days-area]');
      if (!area) return;
      e.preventDefault();
      const step = columnWidth === 'narrow' ? NARROW_COLUMN_WIDTH_PX : WIDE_COLUMN_WIDTH_PX;
      area.scrollBy({ left: e.key === 'ArrowLeft' ? -step : step, behavior: 'smooth' });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [viewMode, drawerType, isMobile, columnWidth]);

  function OnSelectViewModeHandler(value) {
    // value: 0 = daily, 1 = weekly, 2 = monthly, 3 = professionals
    if (value === 2) {
      try { localStorage.setItem('cv3-viewMode', 'monthly'); } catch {}
      setViewMode('monthly');
    } else {
      const days = value === 1 ? 7 : 1;
      const mode = value === 0 ? 'daily' : value === 3 ? 'professionals' : 'weekly';
      try { localStorage.setItem('cv3-viewMode', mode); } catch {}
      setViewMode(mode);
      setViewDaysCount(days);
      setPage(0);
    }
  }

  function onDateChangeByCalendarDatePickerHandler(selectedDate) {
    const selected = new Date(selectedDate);

    if (viewDaysCount > 1) {
      const selectedMonth = selected.getMonth();
      const selectedDay = selected.getDate();
      const dayOfWeek = selected.getDay();
      const startOfWeek = new Date(selected);
      startOfWeek.setDate(selectedDay - dayOfWeek);

      if (startOfWeek.getMonth() !== selectedMonth && selectedDay > 7) {
        const firstDayOfMonth = new Date(selected.getFullYear(), selectedMonth, 1);
        const firstDayOfWeekDay = firstDayOfMonth.getDay();
        const firstSundayOfMonth = new Date(firstDayOfMonth);
        if (firstDayOfWeekDay > 0) {
          firstSundayOfMonth.setDate(1 - firstDayOfWeekDay + 7);
        } else {
          firstSundayOfMonth.setDate(1);
        }
        const weekNumber = Math.floor((selectedDay - 1 + firstDayOfWeekDay) / 7);
        const targetDate = new Date(firstSundayOfMonth);
        targetDate.setDate(firstSundayOfMonth.getDate() + weekNumber * 7);
        const todayCopy = new Date(_today);
        const dayDiff = Math.floor((targetDate - todayCopy) / (1000 * 60 * 60 * 24));
        setPage(Math.floor(dayDiff / 7));
        return;
      }
    }

    const todayCopy = new Date(_today);
    const dayDiff = Math.floor((selected - todayCopy) / (1000 * 60 * 60 * 24));
    setPage(Math.floor(dayDiff / viewDaysCount));
  }

  // ── Handlers de seleção ─────────────────────────────────────────────────────
  function OnItemSelectHandler(appointmentData, nativeEvent) {
    setCurrentAppointment(appointmentData);

    if (appointmentData.status === AppointmensState.Available) {
      const slot = parseDateToSlot(appointmentData.date);
      setDrawerSlot(slot);
      // Coluna de profissional (modo "Profissionais"): novo agendamento já vem com a agenda dele
      setAppointmentInitialValues(appointmentData.resource != null ? { resourceId: appointmentData.resource } : null);
      setSlotAnyProfessional(!!appointmentData.anyProfessional);

      if (nativeEvent && nativeEvent.clientX !== undefined) {
        // Desktop e mobile: abre SlotPopover ancorado ao slot clicado
        // Usa _slotRect (posição do elemento slot) quando disponível,
        // caso contrário usa a posição do clique do mouse como fallback
        const x = nativeEvent._slotRect ? nativeEvent._slotRect.left : nativeEvent.clientX;
        const y = nativeEvent._slotRect ? nativeEvent._slotRect.top : nativeEvent.clientY;
        setPopoverPosition({ x, y });
        setDrawerType('slot_popover');
      } else {
        // Sem evento de posição: abre direto o drawer de novo agendamento
        setDrawerType('new-appointment');
      }
    } else if (appointmentData.occupied) {
      // Horário de outro profissional numa sala, com alcance "Só os meus pacientes": só ocupa, não abre
      return;
    } else if (appointmentData.status === AppointmensState.Blocked && appointmentData.reservationId) {
      // Reserva aguardando o cadastro do paciente → painel da reserva
      setDrawerSlot(parseDateToSlot(appointmentData.date));
      setDrawerAppointment(normalizeAppointment(appointmentData));
      setDrawerType('reservation');
    } else if (appointmentData.status === AppointmensState.Blocked) {
      // Bloqueio existente → drawer de bloqueio (edição)
      const slot = parseDateToSlot(appointmentData.date);
      setDrawerSlot(slot);
      setDrawerAppointment(normalizeAppointment(appointmentData));
      setDrawerType('block');
    } else {
      // Agendamento existente → drawer de detalhes
      setDrawerAppointment(normalizeAppointment(appointmentData));
      setDrawerType('detail');
    }
  }

  function OnHidePopupHandler() {
    setCurrentAppointment(null);
    setPopupId(-1);
  }

  function CreateInvoiceFromAppointmentHandler(data) {
    setCurrentAppointment(data);
    setPopupId(3);
  }

  function ViewInvoiceFromAppointmentHandler(data) {
    setCurrentAppointment(data);
    setPopupId(3);
  }

  // ── Handlers dos drawers v2 ─────────────────────────────────────────────────

  // Todos os callbacks abaixo usam triggerReload() que incrementa reloadCounter.
  // O useEffect de [viewDaysCount, page, viewMode, reloadCounter] reage e chama LoadData
  // sempre com os valores ATUAIS do render — eliminando bugs de stale closure.
  const handleDrawerStatusChange = useCallback((id, newStatus) => {
    triggerReload();
  }, [triggerReload]);

  const handleDrawerReschedule = useCallback((appointment) => {
    setReschedulingAppointment(normalizeAppointment(appointment));
    setDrawerType('reschedule');
  }, []);

  const handleDrawerDelete = useCallback(() => {
    closeDrawer();
    triggerReload();
  }, [closeDrawer, triggerReload]);

  // Chamado pelo PaymentSection após criar/editar fatura:
  // recarrega o calendário e atualiza o drawerAppointment com dados frescos.
  const handleInvoiceCreated = useCallback(() => {
    triggerReload();
  }, [triggerReload]);

  // Chamado pelo AppointmentDetailPanel após edição pontual de campo:
  // atualiza drawerAppointment no pai para manter sincronia e recarrega o calendário.
  const handleAppointmentUpdate = useCallback((updatedAppointment) => {
    setDrawerAppointment(updatedAppointment);
    triggerReload();
  }, [triggerReload]);

  // Etapas marcadas no drawer: corrige o agendamento nas listas em memória, sem
  // recarregar a agenda (o recarregamento deixava cada marcação lenta).
  const handleStepChecksChange = useCallback((appointmentId, stepChecks) => {
    const patch = (list) => (Array.isArray(list) ? list.map((item) => (
      String(item.id) === String(appointmentId) ? { ...item, stepChecks } : item
    )) : list);
    setAllAppointments(patch);
    setMonthlyAppointments(patch);
    setDrawerAppointment((current) => (
      current && String(current.id) === String(appointmentId) ? { ...current, stepChecks } : current
    ));
  }, []);



  const handleConfirmReschedule = useCallback((updatedAppointment, options = {}) => {
    // updatedAppointment vem do ReschedulePanel com:
    //   - date: nova data/hora formatada
    //   - status: 'rescheduled'
    //   - todos os outros campos do agendamento original preservados
    // options.notifyViaSystem: avisar o paciente pelo sistema com o id do NOVO
    //   agendamento (plano Profissional ou superior / número próprio)
    const newApt = updatedAppointment;
    const originalApt = reschedulingAppointment;
    if (!newApt || !originalApt) return;

    // Converte duration de número (minutos) para "HH:MM:SS" — formato esperado pelo calendário
    // normalizeAppointment() converte "HH:MM:SS" → número, mas o calendário precisa de "HH:MM:SS"
    const durationNum = Number(originalApt.duration) || 50;
    const durationHours = Math.floor(durationNum / 60);
    const durationMins = durationNum % 60;
    const durationStr = `${String(durationHours).padStart(2, '0')}:${String(durationMins).padStart(2, '0')}:00`;

    // 1. Marca o agendamento ORIGINAL como reagendado — mantém a data original, só muda status
    //    Também converte duration para "HH:MM:SS" para não corromper o registro original no banco
    //    O pagamento só pode sair do original depois que ele já está como reagendado.
    let originalMarked = false;
    let afterOriginalMarked = null;
    UpdateAppointment({ ...originalApt, status: 'rescheduled', duration: durationStr }, () => {
      originalMarked = true;
      afterOriginalMarked?.();
    });

    // 2. Cria NOVO agendamento com a nova data/hora e todos os dados originais preservados
    //    (patient, resource, procedure, duration, price, roomId, etc.)
    SetAppointment({
      ...originalApt,           // todos os dados originais (patient, resource, procedure, price, roomId)
      id: null,                 // sem id → backend cria novo registro
      status: 'scheduled',      // novo agendamento começa como agendado
      date: newApt.date,        // nova data/hora selecionada no ReschedulePanel
      duration: durationStr,    // "HH:MM:SS" — formato correto para o calendário e banco
    }, (response) => {
      closeDrawer();
      setReschedulingAppointment(null);
      triggerReload();
      // Pelo sistema: aviso com o novo agendamento. Pelo Web: o ReschedulePanel já abriu
      // o WhatsApp no momento do clique, antes da chamada de API (bloqueio de popup).
      const createdId = response?.data?.id ?? null;
      if (options?.notifyViaSystem && createdId) {
        notifyPatientWhatsApp({ type: 'rescheduled', appointment_id: createdId });
      }

      // 3. Já estava pago: o pagamento passa para o novo agendamento
      const hasInvoice = originalApt.invoiceId && String(originalApt.invoiceId) !== '0';
      if (hasInvoice && createdId) {
        const movePayment = () => MoveAppointmentPaymentAPI(originalApt.id, createdId, (moved) => {
          if (moved?.status === 'SUCCESS') {
            triggerReload();
            return;
          }
          setRescheduleWarning('Reagendado, mas o pagamento continua no agendamento antigo.');
        });
        if (originalMarked) movePayment();
        else afterOriginalMarked = movePayment;
      }
    });
  }, [reschedulingAppointment, UpdateAppointment, SetAppointment, closeDrawer, triggerReload, notifyPatientWhatsApp]);



  // ── Popups (apenas CreateInvoicePopup mantido; demais substituídos por drawers) ──
  function GetPopup() {
    switch (popupId) {
      case 3:
        return (
          <CreateInvoicePopup
            Data={currentAppointment}
            OnCreated={LoadData}
            OnHide={OnHidePopupHandler}
          />
        );
      default:
        return null;
    }
  }

  // ── KPIs do servidor ────────────────────────────────────────────────────────
  // Carregados via endpoint dedicado (get_appointments_kpi.php) para garantir
  // dados corretos: lista de espera real, taxa de ocupação baseada na meta da empresa.
  const [serverKpi, setServerKpi] = useState(null);

  useEffect(() => {
    if (viewMode === 'monthly') return; // KPIs só fazem sentido na view semanal/diária
    const startDate = getCurrentViewDate(_today, page, viewDaysCount)
      .toISOString()
      .slice(0, 10);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + viewDaysCount - 1);
    const endDateStr = endDate.toISOString().slice(0, 10);

    GetAppointmentsKpi(startDate, endDateStr, (result) => {
      if (result && !result.error) {
        setServerKpi(result);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, viewDaysCount, viewMode, reloadCounter]);

  // Fallback local enquanto o servidor não responde (ou na view diária sem meta configurada)
  const kpiData = useMemo(() => {
    if (serverKpi) {
      return {
        total: serverKpi.totalAppointments,
        pending: serverKpi.pendingConfirmation,
        waitingRoom: serverKpi.waitingListCount,
        // Meta semanal
        weeklyGoalRate: serverKpi.weeklyGoalRate,
        weeklyGoalCapacity: serverKpi.weeklyGoalCapacity,
        weeklyGoalExcess: serverKpi.weeklyGoalExcess,
        // Taxa de ocupação real
        freeHours: serverKpi.freeHours,
        estimatedFreeSlots: serverKpi.estimatedFreeSlots,
        avgDurationMinutes: serverKpi.avgDurationMinutes,
      };
    }

    // Fallback local (antes do servidor responder)
    const total = filteredAppointments.filter(
      (a) =>
        a.status !== AppointmensState.Available &&
        a.status !== AppointmensState.Blocked
    ).length;
    const pending = filteredAppointments.filter(
      (a) => a.status === AppointmensState.Scheduled
    ).length;
    return {
      total, pending, waitingRoom: 0,
      weeklyGoalRate: 0, weeklyGoalCapacity: 0, weeklyGoalExcess: 0,
      freeHours: null, estimatedFreeSlots: null, avgDurationMinutes: null,
    };
  }, [serverKpi, filteredAppointments]);

  // ── Label de data ───────────────────────────────────────────────────────────
  const viewDate = getCurrentViewDate(_today, page, viewDaysCount);

  // Calcula o mês majoritário (com mais dias na semana visível)
  const _viewDays = getDaysForCurrentView();
  const _monthCounts = _viewDays.reduce((acc, d) => {
    const m = d.getMonth();
    acc[m] = (acc[m] || 0) + 1;
    return acc;
  }, {});
  const _primaryMonth = Object.entries(_monthCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const primaryMonth = _primaryMonth !== undefined ? Number(_primaryMonth) : viewDate.getMonth();

  // Label: "abr e mai de 2026" se há dois meses, senão "maio de 2026"
  const _monthKeys = Object.keys(_monthCounts).map(Number).sort((a, b) => a - b);
  const dateLabelWeekly = _monthKeys.length > 1
    ? `${MONTH_NAMES[_monthKeys[0]]} e ${MONTH_NAMES[_monthKeys[1]]} de ${viewDate.getFullYear()}`
    : `${MONTH_NAMES[viewDate.getMonth()]} de ${viewDate.getFullYear()}`;
  // Modo "Profissionais": as colunas são pessoas, então o dia vai no rótulo
  const dateLabel = viewMode === 'monthly'
    ? `${MONTH_NAMES[monthlyCurrentDate.getMonth()]} de ${monthlyCurrentDate.getFullYear()}`
    : viewMode === 'professionals'
      ? `${WEEKDAY_NAMES[viewDate.getDay()]}, ${viewDate.getDate()} de ${MONTH_NAMES[viewDate.getMonth()]} de ${viewDate.getFullYear()}`
      : dateLabelWeekly;

  // Colunas do modo "Profissionais": agendas marcadas no filtro, na ordem do cadastro
  const professionalColumns = viewMode === 'professionals'
    ? [...filtersByResources]
      .sort((a, b) => a - b)
      .filter((i) => CompanyConfig?.Resources?.[i])
      .map((i) => ({ id: CompanyConfig.Resources[i].Id, label: CompanyConfig.Resources[i].Label }))
    : null;

  // Horário próprio de cada agenda: a coluna do profissional (ou o dia, com uma
  // agenda só no filtro) listra o que fica fora das faixas dele
  const resourceSchedules = Object.fromEntries(
    (CompanyConfig?.Resources || []).map((r) => [String(r.Id), r.Schedule || null])
  );
  // Grade do início mais cedo ao término mais tarde (empresa + horário próprio de
  // cada agenda); quem segue a empresa ganha listra no que passa do horário dela
  const calendarRange = calendarHourRange(CompanyConfig);
  const calendarCompanyHours = calendarRange.widened ? calendarRange.companyHours : null;
  const focusedResourceId = filtersByResources.length === 1
    ? CompanyConfig?.Resources?.[filtersByResources[0]]?.Id ?? null
    : null;
  // Legenda das listras: só quando alguma coluna visível segue um horário próprio
  // ou a empresa tem horários bloqueados
  const visibleOwnHours = professionalColumns
    ? professionalColumns.some((c) => resourceSchedules[String(c.id)]?.hours)
    : !!(focusedResourceId != null && resourceSchedules[String(focusedResourceId)]?.hours);
  const showOffHoursLegend = visibleOwnHours || (CompanyConfig?.BlockedTimes || []).length > 0;

  // Sala do dia de cada coluna (chip sob o nome, só leitura), quando a clínica tem salas.
  // Trocar a sala do dia é no card "Salas de hoje" do Início.
  const dayRoomsDate = professionalColumns && (CompanyConfig?.Rooms || []).length > 0 ? toDayKey(viewDate) : null;
  const { assignments: dayRoomAssignments } = useDayRooms(dayRoomsDate);
  const professionalColumnsWithRooms = (() => {
    if (!professionalColumns || !dayRoomsDate) return professionalColumns;
    const columnResources = professionalColumns
      .map((column) => CompanyConfig.Resources.find((r) => String(r.Id) === String(column.id)))
      .filter(Boolean);
    const summaries = summarizeDayRooms(dayRoomAssignments, columnResources, CompanyConfig.Rooms);
    return professionalColumns.map((column) => {
      const room = summaries.find((s) => s.resourceId === String(column.id));
      return { ...column, room: room?.roomId ? room : undefined };
    });
  })();

  // ─────────────────────────────────────────────────────────────────────────────
  // Contagem de filtros ativos (para badge no botão mobile)
  // ─────────────────────────────────────────────────────────────────────────────
  const totalPossibleStates = usingStatesSource.length;
  const totalPossibleResources = CompanyConfig?.Resources?.length ?? 0;
  const totalPossibleRooms = CompanyConfig?.Rooms?.length ?? 0;
  const totalPossibleProcedures = CompanyConfig?.Procedures?.length ?? 0;
  const totalPossibleDays = 7;

  const activeFiltersCount = (
    (filtersByState.length < totalPossibleStates ? 1 : 0) +
    (totalPossibleResources > 0 && filtersByResources.length < totalPossibleResources ? 1 : 0) +
    (totalPossibleRooms > 0 && filtersByRooms.length < totalPossibleRooms ? 1 : 0) +
    (totalPossibleProcedures > 0 && filtersByProcedures.length < totalPossibleProcedures ? 1 : 0) +
    (filtersByDayOfWeek.length < totalPossibleDays ? 1 : 0) +
    (showOutsideWorkingHours ? 1 : 0)
  );

  // "Localizar profissional" aparece no modo Profissionais com muitas colunas (computador e celular)
  const showGoToResource = viewMode === 'professionals' && (professionalColumns?.length ?? 0) > MANY_AGENDAS_MIN;
  const openResourceMenu = (anchor, mode) => setResourceMenu({ anchor, mode });

  // Menu de agendas: "Localizar profissional" (rola até a coluna) ou "Escolher agenda" (filtra uma só).
  // Com muitas agendas ganha busca por nome.
  const resourceMenuEl = (
    <ResourceMenu
      anchorEl={resourceMenu?.anchor}
      open={Boolean(resourceMenu)}
      onClose={() => setResourceMenu(null)}
      items={resourceMenu?.mode === 'goto'
        ? (professionalColumns || []).map((c) => ({ key: c.id, label: c.label, onPick: () => setFocusColumn({ id: c.id, nonce: Date.now() }) }))
        : (CompanyConfig?.Resources || []).map((r, i) => ({ key: r.Id, label: r.Label, onPick: () => setFiltersByResources([i]) }))}
    />
  );

  // Oportunidade da lista de espera do horário vago aberto no popup (computador e celular)
  const drawerSlotOpportunity = (() => {
    if (drawerType !== 'slot_popover' || !drawerSlot || !opportunitiesMap) return null;
    const pad = (n) => String(n).padStart(2, '0');
    const d = drawerSlot.date;
    if (!(d instanceof Date)) return null;
    const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const time = `${pad(drawerSlot.hour)}:${pad(drawerSlot.minute)}`;
    // Sem coluna por profissional: qualquer agenda, como sempre
    if (!professionalColumns) return getSlotOpportunity(opportunitiesMap, dateStr, time);
    // Selo "Qualquer profissional" do eixo de horas: as entradas com as agendas livres
    if (slotAnyProfessional) {
      return anyProfessionalSlotOpportunity(opportunitiesMap, {
        columnIds: professionalColumns.map((c) => c.id),
        appointments: filteredAppointments,
        date: d,
        dateStr,
        hour: drawerSlot.hour,
        minute: drawerSlot.minute,
        slotIntervalMinutes: getSlotIntervalMinutes(CompanyConfig?.TimeInterval),
      });
    }
    // Coluna do profissional: só quem ele pode atender e com a coluna livre no horário
    const columnResourceId = appointmentInitialValues?.resourceId ?? null;
    const opportunity = getSlotOpportunity(opportunitiesMap, dateStr, time, columnResourceId);
    if (!opportunity) return null;
    const free = isSlotAvailable(filteredAppointments, d, drawerSlot.hour, drawerSlot.minute,
      getSlotIntervalMinutes(CompanyConfig?.TimeInterval), { resourceId: columnResourceId });
    return free ? opportunity : null;
  })();

  // ─────────────────────────────────────────────────────────────────────────────
  // Switch mobile — renderiza layout dedicado para telas pequenas
  // ─────────────────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        {/* Sidebar de filtros — reutilizada do desktop, posicionada como drawer */}
        <aside
          className={[
            styles.sidebar,
            isSidebarOpen ? styles.sidebarOpen : '',
          ].filter(Boolean).join(' ')}
        >
          {/* Mini calendário */}
          <div className={styles.datepickerWrapper}>
            <CalendarDatePicker
              currentDate={getCurrentViewDate(_today, page, viewDaysCount)}
              viewDaysCount={viewDaysCount}
              onChange={onDateChangeByCalendarDatePickerHandler}
            />
          </div>

          {/* Header — título + limpar */}
          <div className={styles.sidebarHeader}>
            <span className={styles.sidebarTitle}>Filtros rápidos</span>
            <button
              type="button"
              className={styles.sidebarClearBtn}
              onClick={() => {
                setFiltersByState(usingStatesSource.map((_, i) => i));
                setFiltersByResources(CompanyConfig?.Resources?.map((_, i) => i) || []);
                setFiltersByRooms(CompanyConfig?.Rooms?.map((_, i) => i) || []);
                setFiltersByProcedures(CompanyConfig?.Procedures?.map((_, i) => i) || []);
                setFiltersByDayOfWeek(CalendarConfig.DaysOfWeek.map((_, i) => i));
              }}
            >
              Limpar
            </button>
          </div>

          {/* Filtros rápidos */}
          <div className={styles.sidebarFilters}>
            <QuickFilterDropdown
              label="Agendas"
              options={CompanyConfig?.Resources?.map((item) => item.Label) || []}
              selected={filtersByResources}
              onChange={setFiltersByResources}
              allLabel="Todos"
              searchable={manyAgendas}
            />
            <QuickFilterDropdown
              label="Salas"
              options={CompanyConfig?.Rooms?.map((item) => item.Label) || []}
              selected={filtersByRooms}
              onChange={setFiltersByRooms}
              allLabel="Todas"
            />
            <QuickFilterDropdown
              label="Situação"
              options={usingStatesSource.map((item) => AppointmensState.GetLabel(item))}
              selected={filtersByState}
              onChange={setFiltersByState}
              allLabel="Todas"
            />
            <QuickFilterDropdown
              label="Procedimentos"
              options={CompanyConfig?.Procedures?.map((item) => item.Label) || []}
              selected={filtersByProcedures}
              onChange={setFiltersByProcedures}
              allLabel="Todos"
            />
            <QuickFilterDropdown
              label="Dia da semana"
              options={CalendarConfig.DaysOfWeek}
              selected={filtersByDayOfWeek}
              onChange={setFiltersByDayOfWeek}
              allLabel="Todos"
            />
            <div className={styles.sidebarCheckRow}>
              <FormControlLabel
                label={
                  <span className={styles.sidebarCheckLabel}>
                    Exibir horários fora do expediente
                  </span>
                }
                control={
                  <Checkbox
                    checked={showOutsideWorkingHours}
                    size="small"
                    sx={{
                      '& .MuiSvgIcon-root': { fontSize: 15 },
                      color: '#CBD5E1',
                      padding: '2px 6px 2px 2px',
                      '&.Mui-checked': { color: '#ED3680' },
                    }}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setShowOutsideWorkingHours(newValue);
                      localStorage.setItem('filterList-workingHours', JSON.stringify(newValue ? [0] : []));
                    }}
                    name="0"
                  />
                }
              />
            </div>
          </div>
        </aside>

        {/* Overlay — fecha sidebar ao clicar fora */}
        {isSidebarOpen && (
          <div
            className={styles.sidebarOverlay}
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Layout mobile dedicado */}
        <AppointmentsMobile
          viewMode={viewMode}
          viewDaysCount={viewDaysCount}
          page={page}
          filteredAppointments={filteredAppointments}
          getDaysForCurrentView={getDaysForCurrentView}
          onPrev={OnPrevClickHandler}
          onNext={OnNextClickHandler}
          onViewModeChange={(mode) => {
            if (mode === 'daily')   OnSelectViewModeHandler(0);
            if (mode === 'weekly')  OnSelectViewModeHandler(1);
            if (mode === 'monthly') OnSelectViewModeHandler(2);
            if (mode === 'professionals') OnSelectViewModeHandler(3);
          }}
          onToday={OnTodayClickHandler}
          onDayClickMonthly={(date) => {
            const todayCopy = new Date(_today);
            const dayDiff = Math.floor((date - todayCopy) / (1000 * 60 * 60 * 24));
            try { localStorage.setItem('cv3-viewMode', 'daily'); } catch {}
            setViewMode('daily');
            setViewDaysCount(1);
            setPage(dayDiff);
          }}
          onAppointmentClick={OnItemSelectHandler}
          onEmptySlotClick={OnItemSelectHandler}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen(v => !v)}
          activeFiltersCount={activeFiltersCount}
          companyConfig={CompanyConfig}
          isUpdating={isUpdating}
          monthlyCurrentDate={monthlyCurrentDate}
          onMonthlyPrev={() => setMonthlyCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
          onMonthlyNext={() => setMonthlyCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
          slotIntervalMinutes={getSlotIntervalMinutes(CompanyConfig?.TimeInterval)}
          onCreateAppointment={() => setDrawerType('create-picker')}
          monthlyAppointments={monthlyAppointments}
          highlightedAppointment={savedFocus.highlighted}
          professionalColumns={professionalColumnsWithRooms}
          resourceSchedules={resourceSchedules}
          focusedResourceId={focusedResourceId}
          calendarRange={calendarRange}
          allAppointments={allAppointments}
          onGoToResource={showGoToResource ? (e) => openResourceMenu(e.currentTarget, 'goto') : null}
          focusColumnId={focusColumn.id}
          focusColumnNonce={focusColumn.nonce}
          slotOpportunities={opportunitiesMap}
        />
        {resourceMenuEl}

        {/* ── Drawers v2 no mobile — via BottomSheet ──────────────────────── */}

        {/* Picker de criação de agendamento (seleção de data/hora) */}
        <BottomSheet
          isOpen={drawerType === 'create-picker'}
          onClose={closeDrawer}
          height="full"
        >
          <CreateAppointmentPicker
            isOpen={drawerType === 'create-picker'}
            onClose={closeDrawer}
            onSlotSelected={handlePickerSlotSelected}
          />
        </BottomSheet>

        {/* Novo agendamento */}
        <BottomSheet
          isOpen={drawerType === 'new-appointment'}
          onClose={closeDrawer}
          height="full"
        >
          <AppointmentDrawer
            isOpen={drawerType === 'new-appointment'}
            slot={drawerSlot}
            appointments={filteredAppointments}
            onClose={closeDrawer}
            onSave={savedFocus.onSaved}
            onSaveSuccess={closeDrawerAndRefresh}
            onNotifyPatient={notifyPatientWhatsApp}
            initialValues={appointmentInitialValues}
          />
        </BottomSheet>

        {/* Bloqueio de horário */}
        <BottomSheet
          isOpen={drawerType === 'block'}
          onClose={closeDrawer}
          height="auto"
        >
          <BlockSlotDrawer
            isOpen={drawerType === 'block'}
            slot={drawerSlot}
            appointment={drawerAppointment}
            onClose={closeDrawer}
            onSave={closeDrawerAndRefresh}
            onGhostChange={setGhostAppointment}
            initialResourceId={appointmentInitialValues?.resourceId ?? focusedResourceId}
          />
        </BottomSheet>

        {/* Reserva para paciente (link de cadastro) */}
        <BottomSheet
          isOpen={drawerType === 'reserve'}
          onClose={closeDrawer}
          height="auto"
        >
          <ReserveSlotDrawer
            isOpen={drawerType === 'reserve'}
            slot={drawerSlot}
            onClose={closeDrawer}
            onCreated={triggerReload}
            onScheduleExisting={handleScheduleExistingPatient}
            initialResourceId={appointmentInitialValues?.resourceId ?? focusedResourceId}
            onGhostChange={setGhostAppointment}
          />
        </BottomSheet>

        {/* Reserva aguardando cadastro */}
        <BottomSheet
          isOpen={drawerType === 'reservation'}
          onClose={closeDrawer}
          height="auto"
        >
          <ReservationPanel
            isOpen={drawerType === 'reservation'}
            appointment={drawerAppointment}
            onClose={closeDrawer}
            onReleased={closeDrawerAndRefresh}
          />
        </BottomSheet>

        {/* Detalhes do agendamento */}
        <BottomSheet
          isOpen={drawerType === 'detail'}
          onClose={closeDrawer}
          height="auto"
        >
          <AppointmentDetailPanel
            isOpen={drawerType === 'detail'}
            appointment={drawerAppointment}
            onClose={closeDrawer}
            onStatusChange={handleDrawerStatusChange}
            onReschedule={handleDrawerReschedule}
            onDelete={handleDrawerDelete}
            onCreateInvoice={(apt) => { setCurrentAppointment(apt); setPopupId(3); closeDrawer(); }}
            onViewInvoice={(apt) => { setCurrentAppointment(apt); setPopupId(3); closeDrawer(); }}
            onInvoiceCreated={handleInvoiceCreated}
            onUpdate={handleAppointmentUpdate}
            onStepChecksChange={handleStepChecksChange}
            onNotifyPatient={notifyPatientWhatsApp}
          />
        </BottomSheet>

        {/* Reagendamento */}
        <BottomSheet
          isOpen={drawerType === 'reschedule'}
          onClose={() => { setDrawerType('detail'); }}
          height="full"
        >
          <ReschedulePanel
            isOpen={drawerType === 'reschedule'}
            appointment={reschedulingAppointment}
            appointments={filteredAppointments}
            onBack={() => { setDrawerType('detail'); }}
            onClose={() => { setDrawerType(null); setReschedulingAppointment(null); }}
            onConfirm={handleConfirmReschedule}
          />
        </BottomSheet>

        {/* SlotPopover — slot vazio mobile (posicionado sobre a tela) */}
        {drawerType === 'slot_popover' && drawerSlot && (
          <SlotPopover
            slot={drawerSlot}
            position={popoverPosition}
            isAvailable={true}
            onNewAppointment={() => {
              popoverNavigatingRef.current = true;
              setDrawerType('new-appointment');
            }}
            onBlockSlot={() => {
              popoverNavigatingRef.current = true;
              setDrawerType('block');
            }}
            onReserveSlot={() => {
              popoverNavigatingRef.current = true;
              setDrawerType('reserve');
            }}
            onCopyDateTime={closeDrawer}
            onClose={() => {
              if (popoverNavigatingRef.current) {
                popoverNavigatingRef.current = false;
                return;
              }
              closeDrawer();
            }}
            opportunity={drawerSlotOpportunity}
            onScheduleFromWaitingList={handleScheduleFromWaitingList}
            onNotifyPatient={notifyPatientWhatsApp}
          />
        )}

        {/* Popup legado — apenas CreateInvoicePopup */}
        {GetPopup()}

        {whatsAppToast}
        {rescheduleWarningToast}
        {waitingListToast}
        {savedFocus.toast}
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // JSX (desktop)
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
    <div className={styles.pageWrapper}>

      <div className={styles.mainLayout}>

        {/* ════════════════════════════════════════════════════
            SIDEBAR DE FILTROS RÁPIDOS
            ════════════════════════════════════════════════════ */}
        <aside
          className={[
            styles.sidebar,
            (isMobile || isTablet) && isSidebarOpen ? styles.sidebarOpen : '',
          ].filter(Boolean).join(' ')}
        >
          {/* Mini calendário */}
          <div className={styles.datepickerWrapper}>
            <CalendarDatePicker
              currentDate={getCurrentViewDate(_today, page, viewDaysCount)}
              viewDaysCount={viewDaysCount}
              onChange={onDateChangeByCalendarDatePickerHandler}
            />
          </div>

          {/* Header — título + limpar */}
          <div className={styles.sidebarHeader}>
            <span className={styles.sidebarTitle}>Filtros rápidos</span>
            <button
              type="button"
              className={styles.sidebarClearBtn}
              onClick={() => {
                setFiltersByState(usingStatesSource.map((_, i) => i));
                setFiltersByResources(CompanyConfig?.Resources?.map((_, i) => i) || []);
                setFiltersByRooms(CompanyConfig?.Rooms?.map((_, i) => i) || []);
                setFiltersByProcedures(CompanyConfig?.Procedures?.map((_, i) => i) || []);
                setFiltersByDayOfWeek(CalendarConfig.DaysOfWeek.map((_, i) => i));
              }}
            >
              Limpar
            </button>
          </div>

          {/* Filtros rápidos — dropdowns compactos */}
          <div className={styles.sidebarFilters}>
            <QuickFilterDropdown
              label="Agendas"
              options={CompanyConfig?.Resources?.map((item) => item.Label) || []}
              selected={filtersByResources}
              onChange={setFiltersByResources}
              allLabel="Todos"
              searchable={manyAgendas}
            />
            <QuickFilterDropdown
              label="Salas"
              options={CompanyConfig?.Rooms?.map((item) => item.Label) || []}
              selected={filtersByRooms}
              onChange={setFiltersByRooms}
              allLabel="Todas"
            />
            <QuickFilterDropdown
              label="Situação"
              options={usingStatesSource.map((item) => AppointmensState.GetLabel(item))}
              selected={filtersByState}
              onChange={setFiltersByState}
              allLabel="Todas"
            />
            <QuickFilterDropdown
              label="Procedimentos"
              options={CompanyConfig?.Procedures?.map((item) => item.Label) || []}
              selected={filtersByProcedures}
              onChange={setFiltersByProcedures}
              allLabel="Todos"
            />
            <QuickFilterDropdown
              label="Dia da semana"
              options={CalendarConfig.DaysOfWeek}
              selected={filtersByDayOfWeek}
              onChange={setFiltersByDayOfWeek}
              allLabel="Todos"
            />

            {/* Horários fora do expediente */}
            <div className={styles.sidebarCheckRow}>
              <FormControlLabel
                label={
                  <span className={styles.sidebarCheckLabel}>
                    Exibir horários fora do expediente
                  </span>
                }
                control={
                  <Checkbox
                    checked={showOutsideWorkingHours}
                    size="small"
                    sx={{
                      '& .MuiSvgIcon-root': { fontSize: 15 },
                      color: '#CBD5E1',
                      padding: '2px 6px 2px 2px',
                      '&.Mui-checked': { color: '#ED3680' },
                    }}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setShowOutsideWorkingHours(newValue);
                      localStorage.setItem('filterList-workingHours', JSON.stringify(newValue ? [0] : []));
                    }}
                    name="0"
                  />
                }
              />
            </div>
          </div>
        </aside>

        {/* ════════════════════════════════════════════════════
            CONTEÚDO PRINCIPAL
            ════════════════════════════════════════════════════ */}
        <main className={styles.content}>

          {/* ── Header da página ── */}
          <div className={styles.pageHeader}>
            <div className={styles.pageHeaderLeft}>
              <div className={styles.pageHeaderIcon}>
                <EventNoteOutlinedIcon sx={{ fontSize: 17 }} />
              </div>
              <h1 className={styles.pageHeaderTitle}>Agendamentos</h1>
              {/* Botão de filtros — visível apenas no tablet (1010px–1566px), no header */}
              {isTablet && (
                <button
                  type="button"
                  className={[
                    styles.headerFilterBtn,
                    isSidebarOpen ? styles.headerFilterBtnActive : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  aria-label="Filtros"
                >
                  <FilterListIcon sx={{ fontSize: 16 }} />
                  Filtros
                  {activeFiltersCount > 0 && (
                    <span className={styles.headerFilterBadge}>{activeFiltersCount}</span>
                  )}
                </button>
              )}
            </div>

            <div className={styles.pageHeaderRight}>
              {isUpdating && (
                <div className={styles.updatingIndicator}>
                  <CircularProgress size={13} thickness={5} />
                  <span>Atualizando…</span>
                </div>
              )}
              {/* Seletor de densidade — oculto no mobile (sempre compacto) */}
              <div className={`${styles.viewModeSelector} ${styles.viewModeSelectorDensity}`}>
                <button
                  type="button"
                  className={`${styles.viewModeBtn} ${density === 'comfortable' ? styles.viewModeBtnActive : ''}`}
                  onClick={() => handleDensityChange('comfortable')}
                  title="Visualização espaçada"
                >
                  <ViewAgendaOutlinedIcon sx={{ fontSize: 13 }} />
                  Espaçado
                </button>
                <button
                  type="button"
                  className={`${styles.viewModeBtn} ${density === 'compact' ? styles.viewModeBtnActive : ''}`}
                  onClick={() => handleDensityChange('compact')}
                  title="Visualização compacta"
                >
                  <ViewHeadlineOutlinedIcon sx={{ fontSize: 13 }} />
                  Compacto
                </button>
              </div>
              {/* Largura das colunas — só no modo "Profissionais", oculto no celular */}
              {viewMode === 'professionals' && (
                <div className={`${styles.viewModeSelector} ${styles.viewModeSelectorDensity}`}>
                  <button
                    type="button"
                    className={`${styles.viewModeBtn} ${columnWidth === 'wide' ? styles.viewModeBtnActive : ''}`}
                    onClick={() => handleColumnWidthChange('wide')}
                    title="Colunas largas: cards mais legíveis"
                  >
                    <ViewColumnOutlinedIcon sx={{ fontSize: 13 }} />
                    Largas
                  </button>
                  <button
                    type="button"
                    className={`${styles.viewModeBtn} ${columnWidth === 'narrow' ? styles.viewModeBtnActive : ''}`}
                    onClick={() => handleColumnWidthChange('narrow')}
                    title="Colunas estreitas: mais agendas na tela, nome curto no topo e cards sem horário"
                  >
                    <ViewCompactOutlinedIcon sx={{ fontSize: 13 }} />
                    Estreitas
                  </button>
                </div>
              )}
              {/* Seletor de modo de visualização */}
              <div className={styles.viewModeSelector}>
                <button
                  type="button"
                  className={`${styles.viewModeBtn} ${viewMode === 'daily' ? styles.viewModeBtnActive : ''}`}
                  onClick={() => OnSelectViewModeHandler(0)}
                >
                  <CalendarViewDayOutlinedIcon sx={{ fontSize: 13 }} />
                  Diário
                </button>
                <button
                  type="button"
                  className={`${styles.viewModeBtn} ${viewMode === 'weekly' ? styles.viewModeBtnActive : ''}`}
                  onClick={() => OnSelectViewModeHandler(1)}
                >
                  <CalendarViewWeekOutlinedIcon sx={{ fontSize: 13 }} />
                  Semanal
                </button>
                <button
                  type="button"
                  className={`${styles.viewModeBtn} ${viewMode === 'monthly' ? styles.viewModeBtnActive : ''}`}
                  onClick={() => OnSelectViewModeHandler(2)}
                >
                  <CalendarViewMonthOutlinedIcon sx={{ fontSize: 13 }} />
                  Mês
                </button>
                <button
                  type="button"
                  className={`${styles.viewModeBtn} ${viewMode === 'professionals' ? styles.viewModeBtnActive : ''}`}
                  onClick={() => OnSelectViewModeHandler(3)}
                  title="O dia com uma coluna por profissional"
                >
                  <ViewWeekOutlinedIcon sx={{ fontSize: 13 }} />
                  Profissionais
                </button>
              </div>
              {/* Botão Criar agendamento — último item à direita */}
              <button
                type="button"
                className={styles.btnCreateAppointment}
                data-tour="agenda-create"
                onClick={() => setDrawerType('create-picker')}
              >
                <AddOutlinedIcon sx={{ fontSize: 15 }} />
                Criar agendamento
              </button>
            </div>
          </div>

          {/* ── Controles do calendário ── */}
          <div className={styles.calendarControls}>
            <div className={styles.calendarControlsLeft}>
              <button className={styles.btnToday} onClick={OnTodayClickHandler} type="button">
                <TodayOutlinedIcon sx={{ fontSize: 13 }} />
                Hoje
              </button>
              <button className={styles.btnArrow} onClick={OnPrevClickHandler} type="button" title="Período anterior" aria-label="Período anterior">
                <ArrowBackIosOutlinedIcon sx={{ fontSize: 12 }} />
              </button>
              <button className={styles.btnArrow} onClick={OnNextClickHandler} type="button" title="Próximo período" aria-label="Próximo período">
                <ArrowForwardIosOutlinedIcon sx={{ fontSize: 12 }} />
              </button>
              <span className={styles.dateLabel}>{dateLabel}</span>
              {/* "Localizar profissional": com muitas colunas, rola direto até o profissional */}
              {showGoToResource && (
                <button
                  type="button"
                  className={styles.btnGoToResource}
                  onClick={(e) => openResourceMenu(e.currentTarget, 'goto')}
                  title="Encontrar a coluna de um profissional e rolar a tela até ela (as setas do teclado também rolam)"
                >
                  <PersonSearchOutlinedIcon sx={{ fontSize: 14 }} />
                  Localizar profissional
                </button>
              )}
            </div>

            {/* KPIs — cards compactos */}
            <div className={styles.kpiRow}>
              <div className={styles.kpiCard}>
                <div className={`${styles.kpiIcon} ${styles.kpiIconBlue}`}>
                  <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
                </div>
                <div className={styles.kpiCardBody}>
                  <span className={styles.kpiValue}><AnimatedNumber value={kpiData.total} /></span>
                  <span className={styles.kpiLabel}>Agendamentos {viewDaysCount === 1 ? 'nesse dia' : 'esta semana'}</span>
                </div>
              </div>
              <div className={styles.kpiCard}>
                <div className={`${styles.kpiIcon} ${styles.kpiIconAmber}`}>
                  <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
                </div>
                <div className={styles.kpiCardBody}>
                  <span className={`${styles.kpiValue} ${styles.kpiAmber}`}><AnimatedNumber value={kpiData.pending} /></span>
                  <span className={styles.kpiLabel}>Pendências para confirmar</span>
                </div>
              </div>
              <div className={styles.kpiCard}>
                <div className={`${styles.kpiIcon} ${styles.kpiIconPurple}`}>
                  <PeopleAltOutlinedIcon sx={{ fontSize: 16 }} />
                </div>
                <div className={styles.kpiCardBody}>
                  <span className={`${styles.kpiValue} ${styles.kpiPurple}`}><AnimatedNumber value={kpiData.waitingRoom} /></span>
                  <span className={styles.kpiLabel}>Lista de espera aguardando</span>
                </div>
              </div>
              {/* KPI 4 — Meta semanal */}
              <div className={styles.kpiCard}>
                <div className={`${styles.kpiIcon} ${styles.kpiIconGreen}`}>
                  <TrendingUpOutlinedIcon sx={{ fontSize: 16 }} />
                </div>
                <div className={styles.kpiCardBody}>
                  {kpiData.weeklyGoalExcess > 0 ? (
                    <>
                      <span className={`${styles.kpiValue} ${styles.kpiAmber}`}>
                        +<AnimatedNumber value={kpiData.weeklyGoalExcess} />
                      </span>
                      <span className={styles.kpiLabel} title={`Meta: ${kpiData.weeklyGoalCapacity} consultas. Realizando ${kpiData.weeklyGoalExcess} a mais.`}>
                        Além da meta ({kpiData.weeklyGoalRate}%)
                      </span>
                    </>
                  ) : (
                    <>
                      <span className={`${styles.kpiValue} ${styles.kpiGreen}`}><AnimatedNumber value={kpiData.weeklyGoalRate} suffix="%" /></span>
                      <span className={styles.kpiLabel}>Meta {viewDaysCount === 1 ? 'do dia' : 'semanal'} ({kpiData.total}/{kpiData.weeklyGoalCapacity})</span>
                    </>
                  )}
                </div>
              </div>
              {/* KPI 5 — Taxa de ocupação real (horários vagos) */}
              {kpiData.freeHours !== null && (
                <div className={styles.kpiCard}>
                  <div className={`${styles.kpiIcon} ${styles.kpiIconRed}`}>
                    <HourglassEmptyOutlinedIcon sx={{ fontSize: 16 }} />
                  </div>
                  <div className={styles.kpiCardBody}>
                    <span className={`${styles.kpiValue} ${kpiData.freeHours === 0 ? styles.kpiGreen : styles.kpiRed}`}>
                      {kpiData.freeHours === 0 ? 'Lotado' : `${kpiData.freeHours}h vagas`}
                    </span>
                    <span className={styles.kpiLabel}>
                      {kpiData.freeHours === 0
                        ? 'Agenda completa esta semana'
                        : `~${kpiData.estimatedFreeSlots} consulta${kpiData.estimatedFreeSlots !== 1 ? 's' : ''} disponíveis`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Conta com muitas agendas no Diário/Semanal sem uma agenda escolhida:
              sugere a visão por profissional ou escolher uma agenda */}
          {!isMobile && manyAgendas && (viewMode === 'daily' || viewMode === 'weekly')
            && focusedResourceId == null && !manyAgendasHintDismissed && (
            <div className={styles.manyAgendasHint} role="status">
              <span className={styles.manyAgendasHintText}>
                Com {totalPossibleResources} agendas, o dia fica mais claro por profissional ou com uma agenda escolhida.
              </span>
              <button type="button" className={styles.manyAgendasHintBtn} onClick={() => OnSelectViewModeHandler(3)}>
                Ver por profissional
              </button>
              <button
                type="button"
                className={styles.manyAgendasHintBtn}
                onClick={(e) => openResourceMenu(e.currentTarget, 'pick')}
              >
                Escolher agenda
              </button>
              <button type="button" className={styles.manyAgendasHintClose} onClick={dismissManyAgendasHint} aria-label="Fechar aviso" title="Fechar">
                <CloseOutlinedIcon sx={{ fontSize: 14 }} />
              </button>
            </div>
          )}

          {resourceMenuEl}

          {/* ── Calendário ── */}
          <div className={styles.calendarWrapper}>
            {viewMode === 'monthly' ? (
              <MonthlyCalendar
                appointments={monthlyAppointments}
                currentDate={monthlyCurrentDate}
                onDayClick={(date) => {
                  // Navega para a view diária do dia clicado
                  const todayCopy = new Date(_today);
                  const dayDiff = Math.floor((date - todayCopy) / (1000 * 60 * 60 * 24));
                  try { localStorage.setItem('cv3-viewMode', 'daily'); } catch {}
                  setViewMode('daily');
                  setViewDaysCount(1);
                  setPage(dayDiff);
                }}
              />
            ) : (
              <Calendar
                days={getDaysForCurrentView()}
                appointments={ghostAppointment ? [...filteredAppointments, ghostAppointment] : filteredAppointments}
                allAppointments={allAppointments}
                startHour={calendarRange.startHour}
                endHour={calendarRange.endHour}
                onAppointmentSelect={OnItemSelectHandler}
                showOutsideWorkingHours={showOutsideWorkingHours}
                slotHeight={slotHeight}
                density={density}
                primaryMonth={primaryMonth}
                availableDaysOfWeek={CompanyConfig?.AvailableDaysOfWeek || null}
                blockedTimes={CompanyConfig?.BlockedTimes || []}
                slotIntervalMinutes={getSlotIntervalMinutes(CompanyConfig?.TimeInterval)}
                activeSlot={drawerType === 'slot_popover' && drawerSlot
                  ? { ...drawerSlot, resourceId: appointmentInitialValues?.resourceId }
                  : null}
                slotOpportunities={opportunitiesMap}
                resourceColumns={professionalColumnsWithRooms}
                resourceSchedules={resourceSchedules}
                focusedResourceId={focusedResourceId}
                companyHours={calendarCompanyHours}
                narrowColumns={columnWidth === 'narrow'}
                focusColumnId={focusColumn.id}
                focusColumnNonce={focusColumn.nonce}
              />
            )}
          </div>

          {/* ── Legenda ── */}
          <div className={styles.legend}>
            <span className={styles.legendTitle}>Situação:</span>
            {getLegendItems().map((item) => (
              <span key={item.label} className={styles.legendItem}>
                <span
                  className={styles.legendDot}
                  style={{ backgroundColor: item.bg }}
                />
                {item.label}
              </span>
            ))}
            {showOffHoursLegend && (
              <>
                <span className={styles.legendSeparator} />
                <span className={styles.legendItem}>
                  <span className={`${styles.legendDot} ${styles.legendDotOffHours}`} />
                  Fora do horário
                </span>
                {visibleOwnHours && (
                  <span className={styles.legendItem}>
                    <span className={`${styles.legendDot} ${styles.legendDotNonWorking}`} />
                    Não atende no dia
                  </span>
                )}
              </>
            )}
          </div>

        </main>
      </div>

      {/* Overlay mobile/tablet — fecha sidebar ao clicar fora */}
      {(isMobile || isTablet) && isSidebarOpen && (
        <div
          className={styles.sidebarOverlay}
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Popup legado — apenas CreateInvoicePopup */}
      {GetPopup()}
    </div>

    {/* Botão flutuante removido — no tablet o botão de filtros fica no pageHeader */}

      {/* ── Drawers v2 (desktop) — renderizados fora do layout para overlay correto ── */}

      {/* SlotPopover — slot vazio desktop (contextual, posicionado no clique) */}
      {/* O SlotPopover chama onClose() internamente após cada ação de botão.
          popoverNavigatingRef evita que onClose sobrescreva o drawerType já definido
          pela ação de navegação (onNewAppointment / onBlockSlot). */}
      {drawerType === 'slot_popover' && drawerSlot && (
        <SlotPopover
          slot={drawerSlot}
          position={popoverPosition}
          isAvailable={true}
          onNewAppointment={() => {
            popoverNavigatingRef.current = true;
            setDrawerType('new-appointment');
          }}
          onBlockSlot={() => {
            popoverNavigatingRef.current = true;
            setDrawerType('block');
          }}
          onReserveSlot={() => {
            popoverNavigatingRef.current = true;
            setDrawerType('reserve');
          }}
          onCopyDateTime={closeDrawer}
          onClose={() => {
            if (popoverNavigatingRef.current) {
              popoverNavigatingRef.current = false;
              return; // ignora — o drawer já foi aberto pela ação de navegação
            }
            closeDrawer();
          }}
          opportunity={drawerSlotOpportunity}
          onScheduleFromWaitingList={handleScheduleFromWaitingList}
          onNotifyPatient={notifyPatientWhatsApp}
        />
      )}

      {/* Drawer lateral direito — novo agendamento */}
      {drawerType === 'new-appointment' && (
        <div className={styles.drawerOverlay} onClick={closeDrawer} role="presentation" />
      )}
      {drawerType === 'new-appointment' && (
        <aside
          className={[
            styles.drawerPanel,
            isDrawerRecurring ? styles.drawerPanelWide : '',
          ].filter(Boolean).join(' ')}
          role="dialog"
          aria-modal="true"
          aria-label="Novo agendamento"
        >
          <AppointmentDrawer
            isOpen
            slot={drawerSlot}
            appointments={filteredAppointments}
            onClose={closeDrawer}
            onSave={() => {}}
            onSaveSuccess={closeDrawerAndRefresh}
            onNotifyPatient={notifyPatientWhatsApp}
            onRecurringModeChange={setIsDrawerRecurring}
            initialValues={appointmentInitialValues}
          />
        </aside>
      )}

      {/* Drawer lateral direito — bloqueio de horário */}
      {drawerType === 'block' && (
        <div className={styles.drawerOverlay} onClick={closeDrawer} role="presentation" />
      )}
      {drawerType === 'block' && (
        <aside className={styles.drawerPanel} role="dialog" aria-modal="true" aria-label="Bloquear horário">
          <BlockSlotDrawer
            isOpen
            slot={drawerSlot}
            appointment={drawerAppointment}
            onClose={closeDrawer}
            onSave={closeDrawerAndRefresh}
            onGhostChange={setGhostAppointment}
            initialResourceId={appointmentInitialValues?.resourceId ?? focusedResourceId}
          />
        </aside>
      )}

      {/* Drawer lateral direito — reserva para paciente */}
      {drawerType === 'reserve' && (
        <div className={styles.drawerOverlay} onClick={closeDrawer} role="presentation" />
      )}
      {drawerType === 'reserve' && (
        <aside className={styles.drawerPanel} role="dialog" aria-modal="true" aria-label="Reservar para paciente">
          <ReserveSlotDrawer
            isOpen
            slot={drawerSlot}
            onClose={closeDrawer}
            onCreated={triggerReload}
            onScheduleExisting={handleScheduleExistingPatient}
            initialResourceId={appointmentInitialValues?.resourceId ?? focusedResourceId}
            onGhostChange={setGhostAppointment}
          />
        </aside>
      )}

      {/* Drawer lateral direito — reserva aguardando cadastro */}
      {drawerType === 'reservation' && (
        <div className={styles.drawerOverlay} onClick={closeDrawer} role="presentation" />
      )}
      {drawerType === 'reservation' && (
        <aside className={styles.drawerPanel} role="dialog" aria-modal="true" aria-label="Reserva de horário">
          <ReservationPanel
            isOpen
            appointment={drawerAppointment}
            onClose={closeDrawer}
            onReleased={closeDrawerAndRefresh}
          />
        </aside>
      )}

      {/* Drawer lateral direito — detalhes do agendamento */}
      {drawerType === 'detail' && (
        <div className={styles.drawerOverlay} onClick={closeDrawer} role="presentation" />
      )}
      {drawerType === 'detail' && (
        <aside className={styles.drawerPanel} role="dialog" aria-modal="true" aria-label="Detalhes do agendamento">
          <AppointmentDetailPanel
            isOpen
            appointment={drawerAppointment}
            onClose={closeDrawer}
            onStatusChange={handleDrawerStatusChange}
            onReschedule={handleDrawerReschedule}
            onDelete={handleDrawerDelete}
            onCreateInvoice={(apt) => { setCurrentAppointment(apt); setPopupId(3); closeDrawer(); }}
            onViewInvoice={(apt) => { setCurrentAppointment(apt); setPopupId(3); closeDrawer(); }}
            onInvoiceCreated={handleInvoiceCreated}
            onUpdate={handleAppointmentUpdate}
            onStepChecksChange={handleStepChecksChange}
            onNotifyPatient={notifyPatientWhatsApp}
          />
        </aside>
      )}

      {/* Drawer lateral direito — picker de criação */}
      {drawerType === 'create-picker' && (
        <div className={styles.drawerOverlay} onClick={closeDrawer} role="presentation" />
      )}
      {drawerType === 'create-picker' && (
        <aside className={styles.drawerPanel} role="dialog" aria-modal="true" aria-label="Criar agendamento">
          <CreateAppointmentPicker
            isOpen
            onClose={closeDrawer}
            onSlotSelected={handlePickerSlotSelected}
          />
        </aside>
      )}

      {/* Drawer lateral direito — reagendamento */}
      {drawerType === 'reschedule' && (
        <div className={styles.drawerOverlay} onClick={() => { setDrawerType('detail'); }} role="presentation" />
      )}
      {drawerType === 'reschedule' && (
        <aside className={styles.drawerPanel} role="dialog" aria-modal="true" aria-label="Reagendar">
          <ReschedulePanel
            isOpen
            appointment={reschedulingAppointment}
            appointments={filteredAppointments}
            onBack={() => { setDrawerType('detail'); }}
            onClose={() => { setDrawerType(null); setReschedulingAppointment(null); }}
            onConfirm={handleConfirmReschedule}
          />
        </aside>
      )}

      {whatsAppToast}
      {rescheduleWarningToast}
      {waitingListToast}
    </>
  );
}

export default Appointments;
